/**
 * Token Expiration Warning Banner
 *
 * Exibe aviso quando o token do Meta Ads está próximo de expirar (< 7 dias)
 * ou já expirou. Visível apenas para administradores.
 *
 * Comportamento:
 * - Pode ser ocultado temporariamente
 * - Reaparece ao navegar para outra página
 * - Mostra dias restantes e link para configurações
 */

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AlertCircle, AlertTriangle, X, Settings, PlayCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/features/auth';
import { useTokenStatus } from '@/hooks/useMetaAdsConfig';
import { cn } from '@/lib/utils';
import { META_ADS_TUTORIAL_URL } from '@/constants/external-links';

export function TokenExpirationWarning() {
  const { profile } = useAuth();
  const { tokenStatus, isLoading } = useTokenStatus();
  const location = useLocation();
  const [isHidden, setIsHidden] = useState(false);

  // Resetar estado de "oculto" ao mudar de página
  useEffect(() => {
    setIsHidden(false);
  }, [location.pathname]);

  // Não mostrar se:
  // - Não é admin
  // - Está carregando
  // - Não há status de token
  // - Token está válido
  // - Banner foi ocultado manualmente
  if (!profile || profile.role !== 'admin') return null;
  if (isLoading || !tokenStatus) return null;
  if (tokenStatus.status === 'valido') return null;
  if (isHidden) return null;

  const isExpired = tokenStatus.status === 'expirado';
  const daysLeft = tokenStatus.days_until_expiration;

  return (
    <Alert
      variant="destructive"
      className={cn(
        "mx-6 mt-4 relative",
        isExpired
          ? "bg-red-50 border-red-500 text-red-900 dark:bg-red-950 dark:text-red-100"
          : "bg-yellow-50 border-yellow-500 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-100"
      )}
    >
      {isExpired ? (
        <AlertCircle className="h-4 w-4" />
      ) : (
        <AlertTriangle className="h-4 w-4" />
      )}

      <AlertTitle className="font-semibold">
        {isExpired ? 'Token do Meta Ads Expirado!' : 'Token do Meta Ads Expirando em Breve'}
      </AlertTitle>

      <AlertDescription className="mt-2 flex items-center justify-between gap-4">
        <span>
          {isExpired
            ? 'O token de acesso à API do Meta Ads expirou. Atualize imediatamente para continuar coletando dados de anúncios.'
            : `O token de acesso à API do Meta Ads expira em ${daysLeft} ${daysLeft === 1 ? 'dia' : 'dias'}. Atualize para evitar interrupções na coleta de dados.`
          }
        </span>

        <div className="flex items-center gap-2">
          <Button
            asChild
            size="sm"
            variant={isExpired ? "destructive" : "default"}
            className={cn(
              "whitespace-nowrap",
              !isExpired && "bg-yellow-600 hover:bg-yellow-700 text-white dark:bg-yellow-700 dark:hover:bg-yellow-800"
            )}
          >
            <Link to="/settings">
              <Settings className="h-4 w-4 mr-2" />
              Atualizar Token
            </Link>
          </Button>

          <Button
            asChild
            size="sm"
            variant="outline"
            className={cn(
              "whitespace-nowrap",
              isExpired
                ? "border-red-300 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-950"
                : "border-yellow-300 hover:bg-yellow-50 dark:border-yellow-800 dark:hover:bg-yellow-950"
            )}
          >
            <a href={META_ADS_TUTORIAL_URL} target="_blank" rel="noopener noreferrer">
              <PlayCircle className="h-4 w-4 mr-2" />
              Ver Tutorial
            </a>
          </Button>

          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsHidden(true)}
            className={cn(
              "px-2",
              isExpired
                ? "hover:bg-red-100 dark:hover:bg-red-900"
                : "hover:bg-yellow-100 dark:hover:bg-yellow-900"
            )}
            title="Ocultar temporariamente"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
